import { useCallback, useEffect, useMemo, useRef } from "react";

import { type EmblaCarouselType } from "embla-carousel";

import { type ServerDataOutput } from "../data.subscription-data";

interface UseCarouselParams {
  data: ServerDataOutput;
  emblaApi: EmblaCarouselType | undefined;
  onChange?: (v: string) => void;
  value?: string;
}

/**
 * Sync carousel state with query params.
 */
export const useCarouselParams = (props: UseCarouselParams): void => {
  const { data, emblaApi, value, onChange } = props;

  const {
    subscriptionVariant: {
      subscriptionPackageVariant: { variantOptionsArray },
    },
  } = data;

  // This will store targeted carousel index
  const carouselIdx = useRef<null | number>(null);

  // Derive index from query param
  const idxByParams = useMemo(() => {
    return variantOptionsArray.findIndex(({ optionKey }) => {
      return optionKey === value;
    });
  }, [value, variantOptionsArray]);

  // Reflect query param -> carousel
  useEffect(() => {
    if (emblaApi && carouselIdx.current !== idxByParams) {
      emblaApi.scrollTo(idxByParams, true);
      carouselIdx.current = idxByParams;
    }
  }, [emblaApi, idxByParams]);

  // Sync carousel -> query param
  const onSelect = useCallback(
    (emApi: EmblaCarouselType) => {
      if (!onChange) {
        return;
      }

      const selectedIdx = emApi.selectedScrollSnap();
      const { optionKey } = variantOptionsArray[selectedIdx];
      carouselIdx.current = selectedIdx;
      onChange(optionKey);
    },
    [onChange, variantOptionsArray],
  );
  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);
};
